import React, { FC, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { IChecklistReview, IChecklistReviewJob } from '../../../models';
import {
  Autocomplete,
  FormControl,
  MenuItem,
  Grid,
  Button,
  Divider,
  TextField
} from '@mui/material';
import { FilterList, Close, ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import {
  getPlaceOrderChecklistReviewsJobs,
  getPlaceOrderChecklistReviewsArchiveJobs
} from '../../../fetch';
import { SMBSelect } from '../../../components';
import { DatePicker } from '@mui/x-date-pickers';

interface IChecklistReviewFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  selectedType: string;
  setSelectedType: (val: string) => void;
  selectedJob: string;
  setSelectedJob: (val: string) => void;
  hasAppliedFilters: boolean;
  setHasAppliedFilters: (val: boolean) => void;
  isArchivedChecklists: boolean;
  completedOnDate?: Date;
  setCompletedOnDate?: React.Dispatch<React.SetStateAction<Date>>;
  setSelectedChecklists?: React.Dispatch<React.SetStateAction<IChecklistReview[]>>;
}

export const ChecklistReviewFilters: FC<IChecklistReviewFilters> = ({
  hasAppliedFilters,
  setHasAppliedFilters,
  isLoading,
  applyFilters,
  selectedType,
  setSelectedType,
  setSelectedJob,
  selectedJob,
  isArchivedChecklists,
  completedOnDate,
  setCompletedOnDate,
  setSelectedChecklists
}) => {
  const [isMobileFilterButtonOpen, toggleMobileFilter] = useState<boolean>(false);
  const [isLoadingJobs, setLoadingJobs] = useState<boolean>(false);
  const [jobs, setJobs] = useState<IChecklistReviewJob[]>([]);
  const [selectedNameJob, setSelectedNameJob] = useState<string>('');
  const classes = useStyles({ isMobileFilterButtonOpen });
  const fetchChecklistJobs = async () => {
    setLoadingJobs(true);
    try {
      let res = null;

      if (isArchivedChecklists) {
        res = await getPlaceOrderChecklistReviewsArchiveJobs();
      } else {
        res = await getPlaceOrderChecklistReviewsJobs({
          isOpenEnded: selectedType === 'null' ? null : selectedType === 'true' ? true : false
        });
      }

      setJobs(res);
    } catch (error) {
    } finally {
      setLoadingJobs(false);
    }
  };

  useEffect(() => {
    fetchChecklistJobs();
    setSelectedNameJob('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArchivedChecklists]);

  return (
    <>
      <Button
        color="secondary"
        variant="contained"
        className={classes.mobileButton}
        startIcon={<FilterList />}
        endIcon={isMobileFilterButtonOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        onClick={() => {
          toggleMobileFilter(!isMobileFilterButtonOpen);
        }}
      >
        Filters
      </Button>
      {!isMobileFilterButtonOpen && <Divider className={classes.divider} />}
      <Grid container spacing={1} alignItems="center" className={classes.wrapper}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth variant="outlined" size="small">
            {isArchivedChecklists ? (
              <Autocomplete
                value={selectedNameJob}
                onChange={(event, newValue: string) => {
                  const selected = jobs.find(u => u.description === newValue);
                  if (selected) {
                    setSelectedJob(`${selected.jobSageId}`);
                  }
                  if (newValue && newValue.length > 0) {
                    setSelectedNameJob(newValue);
                  } else {
                    setSelectedNameJob('');
                  }
                }}
                id="job"
                options={jobs.map(p => p.description)}
                disabled={isLoading || isLoadingJobs}
                getOptionLabel={(option: string) => {
                  return option;
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    size="small"
                    name="name"
                    autoComplete="none"
                    label="Job"
                    variant="outlined"
                  />
                )}
              />
            ) : (
              <>
                <SMBSelect
                  variant="outlined"
                  onChange={({ target: { value } }) => setSelectedJob(value)}
                  label="Job"
                  value={selectedJob}
                  disabled={isLoading || isLoadingJobs}
                  handleClear={() => {
                    setSelectedJob('');
                  }}
                >
                  {jobs.map((job, index) => {
                    return (
                      <MenuItem key={`${index}`} value={job.jobSageId}>
                        {job.description}
                      </MenuItem>
                    );
                  })}
                </SMBSelect>
              </>
            )}
          </FormControl>
        </Grid>
        {!isArchivedChecklists && (
          <Grid item xs={12} sm={6} md={3}>
            <SMBSelect
              variant="outlined"
              label="PO Type"
              value={selectedType}
              onChange={({ target: { value } }) => setSelectedType(value)}
              disabled={isLoading || isLoadingJobs}
              handleClear={() => {
                setSelectedType('');
              }}
            >
              {[
                { label: 'All PO Types', value: 'null' },
                { label: 'All PO Types Except Open Ended', value: 'false' },
                { label: 'Open Ended PO Types', value: 'true' }
              ].map((type, index) => {
                return (
                  <MenuItem key={`${index}`} value={type.value}>
                    {type.label}
                  </MenuItem>
                );
              })}
            </SMBSelect>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={3}>
          <DatePicker
            label="Completed On"
            value={completedOnDate}
            disabled={isLoading}
            onChange={val => setCompletedOnDate(val)}
            maxDate={new Date()}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Button
            color="primary"
            variant="contained"
            className={classes.button}
            disabled={isLoading}
            startIcon={<FilterList />}
            onClick={() => {
              setHasAppliedFilters(true);
              applyFilters();
            }}
          >
            Apply Filters
          </Button>
          {hasAppliedFilters && (
            <Button
              className={clsx(classes.button, classes.resetButton)}
              variant="contained"
              disabled={isLoading}
              color="inherit"
              startIcon={<Close />}
              onClick={() => {
                setHasAppliedFilters(false);
                setSelectedType('null');
                setSelectedJob('');
                setSelectedNameJob('');
                applyFilters(true);
                setCompletedOnDate(null);
                setSelectedChecklists([]);
              }}
            >
              Reset
            </Button>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const useStyles = makeStyles<Theme, { isMobileFilterButtonOpen?: boolean }>((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  button: {
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    '@media (min-width: 400px)': {
      width: '48%'
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    '@media (min-width: 400px)': {
      marginLeft: 11
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    }
  },
  wrapper: ({ isMobileFilterButtonOpen }) =>
    isMobileFilterButtonOpen
      ? {
          marginTop: 10,
          marginBottom: 10,
          display: 'flex'
        }
      : {
          display: 'none',
          marginBottom: theme.spacing(1),
          [theme.breakpoints.up('sm')]: {
            display: 'flex',
            marginBottom: theme.spacing(1),
            marginTop: theme.spacing(0.5)
          }
        },
  divider: {
    display: 'block',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  gridItem: {
    padding: `3px 8px !important`,
    [theme.breakpoints.up('sm')]: {
      padding: `8px !important`
    }
  },
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  },
  ellipsis: {
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible'
    }
  }
}));
